import { memo } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { MenuSmallIcon } from '@/icons';
import { cn } from '@/utils';
import { useImageUploadInput } from '@/views/build/hook/useImageUploadInput';
import { useFocusTarget } from '../../focus/useFocusTarget';
import { showImageUploadModal } from '../../modals/ImageUpload/ImageUploadModal';
import { DeleteButton } from '../DeleteButton';
import { EditTools } from '../EditTools';
import { HeightAnimate } from '../HeightAnimate';
import { useDroppableImageUploadInput } from '../hook/useDroppableImageUploadInput';
import { usePasteFile } from '../hook/usePasteFile';
import { useQuizFormField } from '../hook/useQuizFormField';
import { useQuizControl } from '../QuizControlProvider';
import { TextEditor } from '../TextEditor';
import { AiSingleChoiceGeneration } from './AiSingleChoiceGeneration';
import { AnswerControl } from './AnswerControl';
import { MultipleOptionImage } from './MultipleOptionImage';
import { useAiGenerateMultipleStatusValue } from './useAiGenerateMultipleStatus';
import { useAnswerControl } from './useAnswerControl';
import { useMultipleOptionTextEditor } from './useMultipleOptionTextEditor';
import { useOptionSortable } from './useOptionSortable';
type EditableMultipleItemProps = {
  id: string;
  isEditMode: boolean;
  isLastOption: boolean;
  onRemove?: () => void;
  choiceIndex: number;
};
export const EditableMultipleItem = memo(({
  onRemove,
  choiceIndex,
  id,
  isEditMode,
  isLastOption
}: EditableMultipleItemProps) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    index
  } = useQuizControl();
  const [imageUrl, updateImageUrl] = useQuizFormField({
    field: `multipleData.choiceList.${choiceIndex}.imageUrl`
  });
  const {
    isAnswer,
    handleToggle
  } = useAnswerControl({
    index: choiceIndex
  });
  const choiceRef = useFocusTarget<HTMLDivElement>(`quizList.${index}.multipleData.choiceList.${choiceIndex}.text`);
  const {
    generatingIndexes,
    isGeneratingBulk
  } = useAiGenerateMultipleStatusValue();
  const isGeneratingItem = generatingIndexes.includes(choiceIndex);
  const isGeneratingInScope = generatingIndexes.length > 0;
  const sortDisabled = isGeneratingInScope || isGeneratingBulk;
  const {
    editor
  } = useMultipleOptionTextEditor({
    choiceIndex,
    isLastOption
  });
  const {
    attributes,
    listeners,
    setNodeRef,
    style,
    isDragging: isOptionDragging
  } = useOptionSortable({
    id: id,
    disabled: sortDisabled
  });
  const {
    handleFileChange,
    accept,
    isUploading
  } = useImageUploadInput({
    onUploadComplete: imageUrl => {
      updateImageUrl(imageUrl);
    }
  });
  const handleFileChangeVoid = (file: File | null) => {
    void handleFileChange(file);
  };
  const {
    rootProps,
    inputProps,
    isDragActive,
    inputRef
  } = useDroppableImageUploadInput({
    onFileChange: handleFileChangeVoid,
    accept,
    imageUrl: imageUrl,
    disabled: false
  });
  usePasteFile({
    editor,
    shouldConfirm: !!imageUrl,
    onPaste: handleFileChangeVoid
  });
  return <div {...listeners} {...attributes} ref={setNodeRef} style={style} onClick={e => {
    e.stopPropagation();
  }} className={cn(isOptionDragging && 'shadow-md', isEditMode && 'cursor-default')}>
        <div className={cn('flex w-full flex-col gap-2xs group')} {...rootProps}>
          <HeightAnimate className="relative">
            <input {...inputProps} className="hidden" ref={inputRef} />
            <div className="relative flex flex-row items-center gap-xs">
              <MenuSmallIcon size="xs" fill={ICON_COLOR.alternative} className={cn('absolute -translate-x-[18px] opacity-0 transition-opacity duration-200 group-hover:opacity-100', 'cursor-grab', isOptionDragging && 'cursor-grabbing', sortDisabled && 'hidden')} />
              <AnswerControl isChecked={isAnswer} onToggle={handleToggle} className="cursor-pointer self-start pt-[5px]" disabled={isGeneratingItem} />
              <div className="relative flex min-h-px min-w-px grow basis-0 flex-col items-start justify-center gap-2xs">
                <AiSingleChoiceGeneration.Overlay isVisible={isGeneratingItem && !isGeneratingBulk} />
                <TextEditor.Input ref={choiceRef} placeholder={t('createMultiple.choice.placeholderV2', {
              n: choiceIndex + 1
            })} placeholderClassName={cn('text-heading-sm-bold', isGeneratingItem && 'text-transparent')} editor={editor} editorClassName="text-heading-sm-bold" className={cn('text-heading-sm-bold', isDragActive && 'shadow-[inset_0_-2px_0_0_theme(colors.violet.300)]', !isAnswer && 'text-assistive', 'focus-within:text-default', isGeneratingItem && 'text-transparent focus-within:text-transparent')} onClick={e => {
              e.stopPropagation();
            }} />
              </div>
              {onRemove && isEditMode && <div className="flex w-[30px] shrink-0 items-start justify-center px-0 py-2xs">
                  <DeleteButton onClick={onRemove} disabled={isGeneratingInScope} />
                </div>}
            </div>

            <EditTools.InlineTooltipContainer className={'ml-[39px] hidden group-focus-within:flex'}>
              <TextEditor.EquationButton editor={editor} className="hidden tablet-lg:flex" />
              <EditTools.Divider className="hidden tablet-lg:flex" />
              <EditTools.ImageAddButton onClick={() => {
            showImageUploadModal({
              onUploadStart: handleFileChangeVoid,
              imageUrl: imageUrl
            });
          }} />
              <EditTools.Divider />
              <AiSingleChoiceGeneration.Button choiceIndex={choiceIndex} />
            </EditTools.InlineTooltipContainer>
          </HeightAnimate>

          {imageUrl && <MultipleOptionImage imageUrl={imageUrl} isUploading={isUploading} onRemove={() => updateImageUrl(undefined)} />}
        </div>
      </div>;
});